<template>
  <v-dialog v-model="dialogVisible" max-width="560" persistent>
    <v-card class="pa-6">
      <!-- Header -->
      <div class="pdf-upload-header px-4 py-3 rounded-lg d-flex align-center justify-space-between mb-4">
        <div class="d-flex align-center gap-3">
          <div class="upload-icon-wrap mr-3">
            <v-icon color="white" size="22">mdi-file-pdf-box</v-icon>
          </div>
          <div>
            <div class="text-white font-weight-black text-base">Upload Lesson PDF</div>
            <div class="text-white text-xs opacity-70">PDF format — max 50 MB</div>
          </div>
        </div>
        <v-btn icon dark @click="close" :disabled="uploading">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pa-0">
        <!-- Step 1: File picker -->
        <div v-if="!uploading && !uploadDone">
          <div
            class="drop-zone rounded-xl d-flex flex-column align-center justify-center pa-8 my-4"
            :class="{ 'drop-zone--active': dragOver }"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop.prevent="onDrop"
            @click="$refs.fileInput.click()"
          >
            <v-icon size="48" :color="dragOver ? '#d32f2f' : '#bdbdbd'" class="mb-3"
              >mdi-file-document-outline</v-icon
            >
            <div class="text-sm font-weight-bold grey--text text--darken-2 mb-1">
              {{ selectedFile ? selectedFile.name : 'Click or drag & drop PDF here' }}
            </div>
            <div v-if="selectedFile" class="text-xs grey--text">
              {{ (selectedFile.size / (1024 * 1024)).toFixed(1) }} MB
            </div>
            <div v-else class="text-xs grey--text">Supported format: PDF</div>
          </div>
          <input
            ref="fileInput"
            type="file"
            accept="application/pdf"
            class="d-none"
            @change="onFileChange"
          />

          <v-text-field
            v-model="contentTitle"
            label="Content Title"
            outlined
            dense
            prepend-inner-icon="mdi-subtitles-outline"
            hide-details="auto"
            class="mb-4"
            placeholder="e.g. Reference Guide for Collections"
          />

          <div class="d-flex justify-end gap-2">
            <v-btn text class="normal-case rounded-lg" @click="close">Cancel</v-btn>
            <v-btn
              color="#d32f2f"
              class="white--text normal-case px-5 rounded-lg"
              depressed
              :disabled="!selectedFile || !contentTitle.trim()"
              @click="startUpload"
            >
              <v-icon left small>mdi-upload</v-icon>
              Upload PDF
            </v-btn>
          </div>
        </div>

        <!-- Step 2: Uploading / Done -->
        <div v-else class="text-center py-4">
          <div class="mb-5">
            <v-progress-circular
              v-if="uploading && uploadProgress < 100"
              :value="uploadProgress"
              size="72"
              width="6"
              color="#d32f2f"
              class="mb-3"
            >
              <span class="text-sm font-weight-black" style="color: #d32f2f"
                >{{ uploadProgress }}%</span
              >
            </v-progress-circular>

            <div v-else-if="uploading && uploadProgress === 100">
              <v-progress-circular indeterminate color="#d32f2f" size="72" width="6" class="mb-3" />
              <div class="text-sm font-weight-medium grey--text text--darken-2">
                Saving to course…
              </div>
            </div>

            <v-icon v-else-if="uploadDone && !uploadError" size="72" color="success" class="mb-3"
              >mdi-check-circle</v-icon
            >
            <v-icon v-else-if="uploadError" size="72" color="error" class="mb-3"
              >mdi-alert-circle</v-icon
            >
          </div>

          <template v-if="uploading && uploadProgress < 100">
            <div class="text-base font-weight-bold grey--text text--darken-3 mb-1">
              Uploading PDF…
            </div>
            <div class="text-xs grey--text">{{ selectedFile && selectedFile.name }}</div>
          </template>

          <template v-else-if="uploadDone && !uploadError">
            <div class="text-base font-weight-bold success--text mb-1">Upload Successful!</div>
            <div class="text-sm grey--text mb-4">
              The PDF has been uploaded and linked to this lesson.
            </div>
            <div class="d-flex justify-center">
              <v-btn color="#21427d" class="white--text normal-case" depressed @click="close"
                >Done</v-btn
              >
            </div>
          </template>

          <template v-else-if="uploadError">
            <div class="text-base font-weight-bold error--text mb-2">Upload Failed</div>
            <div class="text-sm grey--text mb-4">{{ uploadError }}</div>
            <div class="d-flex justify-center gap-3">
              <v-btn text class="normal-case" @click="resetUpload">Try Again</v-btn>
              <v-btn text class="normal-case" @click="close">Cancel</v-btn>
            </div>
          </template>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'PdfUploadDialog',
  props: {
    value: { type: Boolean, default: false },
    courseId: { type: String, required: true },
    moduleId: { type: String, required: true },
    lessonId: { type: String, required: true }
  },
  data() {
    return {
      selectedFile: null,
      contentTitle: '',
      dragOver: false,
      uploading: false,
      uploadProgress: 0,
      uploadDone: false,
      uploadError: null
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    }
  },
  methods: {
    onFileChange(e) {
      const file = e.target.files[0]
      if (file) this.selectedFile = file
    },
    onDrop(e) {
      this.dragOver = false
      const file = e.dataTransfer.files[0]
      if (file && file.type === 'application/pdf') this.selectedFile = file
    },
    async startUpload() {
      if (!this.selectedFile || !this.contentTitle.trim()) return
      this.uploading = true
      this.uploadProgress = 0
      this.uploadError = null
      try {
        const formData = new FormData()
        formData.append('file', this.selectedFile)
        formData.append('courseId', this.courseId)
        formData.append('moduleId', this.moduleId)
        formData.append('lessonId', this.lessonId)
        formData.append('contentTitle', this.contentTitle.trim())

        const res = await this.$axios.post('/learning_center/video/upload-pdf', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            this.uploadProgress = Math.round((e.loaded * 100) / e.total)
          }
        })
        this.uploadDone = true
        this.$emit('uploaded', {
          contentId: res.data.contentId,
          contentTitle: this.contentTitle.trim(),
          contentType: 'PDF',
          contentUrl: res.data.contentUrl
          // hlsStatus: 'PROCESSING'
        })
      } catch (err) {
        this.uploadError = err?.response?.data?.message || err.message || 'Upload failed'
      } finally {
        this.uploading = false
      }
    },
    resetUpload() {
      this.selectedFile = null
      this.contentTitle = ''
      this.uploading = false
      this.uploadProgress = 0
      this.uploadDone = false
      this.uploadError = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    close() {
      if (this.uploading) return
      this.resetUpload()
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped>
.pdf-upload-header {
  background: linear-gradient(135deg, #d32f2f 0%, #ef5350 100%);
}
.upload-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.drop-zone {
  border: 2px dashed #f5c2c2;
  cursor: pointer;
  transition: all 0.25s ease;
  min-height: 150px;
}
.drop-zone:hover,
.drop-zone--active {
  border-color: #d32f2f;
  background: #fdf2f2;
}
</style>
